const {
  sha256,
  bytesToHex,
  hexToBytes,
  textEncoder,
  textDecoder,
} = require('./utils')
const { sign, verify } = require('./crypto')
const { ENCODING_FORMATS } = require('./constants')

/**
 * @typedef {import('./types').WorkerRequest} WorkerRequest
 */

/**
 * Packs a payload into a signed WorkerRequest
 *
 * Process:
 * 1. Serialize payload to JSON
 * 2. Compute SHA-256 hash of payload
 * 3. Sign hash with Ed25519 private key
 * 4. Encode everything as hex strings
 *
 * @param {Object} params
 * @param {Uint8Array} params.publicKey - Sender's Ed25519 public key
 * @param {Uint8Array} params.privKey - Sender's Ed25519 private key
 * @param {Object} params.payload - Payload object to pack
 * @returns {WorkerRequest} Signed worker request
 */
const packWorkerRequest = ({ publicKey, privKey, payload }) => {
  // Serialize payload to JSON bytes
  const payloadBytes = textEncoder.encode(JSON.stringify(payload))

  // Hash the payload (we sign the hash, not the full payload)
  const hash = sha256(payloadBytes)

  // Sign the hash with Ed25519
  const signature = sign(privKey, hash)

  return {
    payload: bytesToHex(payloadBytes),
    public_key: bytesToHex(publicKey),
    signature: bytesToHex(signature),
    encoding: ENCODING_FORMATS.HEX,
  }
}

/**
 * Unpacks and verifies a WorkerRequest, returning the payload
 *
 * Process:
 * 1. Verify signature is valid
 * 2. Decode hex payload to bytes
 * 3. Parse JSON payload
 *
 * @param {WorkerRequest} request - Worker request to unpack
 * @returns {Object} Parsed payload object
 * @throws {Error} If signature verification fails
 */
const unpackWorkerRequestPayload = (request) => {
  // Verify signature first (throws if invalid)
  verifyWorkerRequestSignature(request)

  // Decode payload from hex to bytes
  const payloadBytes = hexToBytes(request.payload)

  // Parse JSON and return
  return JSON.parse(textDecoder.decode(payloadBytes))
}

/**
 * Verifies the Ed25519 signature of a WorkerRequest
 *
 * The signature is computed over the SHA-256 hash of the payload.
 * This proves the request came from the holder of the private key
 * corresponding to public_key.
 *
 * @param {WorkerRequest} request - Worker request to verify
 * @param {string} request.public_key - Hex-encoded public key
 * @param {string} request.payload - Hex-encoded payload
 * @param {string} request.signature - Hex-encoded Ed25519 signature
 * @throws {Error} If signature is invalid
 */
const verifyWorkerRequestSignature = ({ public_key, payload, signature }) => {
  // Compute hash of the payload (same as during signing)
  const hash = sha256(hexToBytes(payload))

  // Verify Ed25519 signature
  const isValid = verify(hexToBytes(public_key), hash, hexToBytes(signature))

  if (!isValid) {
    throw new Error('signature is not valid')
  }
}

/**
 * Validates that a WorkerRequest has all required fields
 *
 * Note: This only checks field presence, not signature validity.
 * Use verifyWorkerRequestSignature() for cryptographic verification.
 *
 * @param {WorkerRequest} request - Worker request to validate
 * @throws {Error} If required fields are missing
 */
const validateWorkerRequest = (request) => {
  if (!request.public_key) {
    throw new Error('public_key is required')
  }

  if (!request.signature) {
    throw new Error('signature is required')
  }

  if (!request.payload) {
    throw new Error('payload is required')
  }
}

/**
 * Fully validates a WorkerRequest (fields + signature)
 *
 * This is a convenience method that combines field validation
 * and signature verification.
 *
 * @param {WorkerRequest} request - Worker request to validate
 * @throws {Error} If validation or verification fails
 */
const validateAndVerifyWorkerRequest = (request) => {
  validateWorkerRequest(request)
  verifyWorkerRequestSignature(request)
}

module.exports = {
  packWorkerRequest,
  unpackWorkerRequestPayload,
  validateWorkerRequest,
  validateAndVerifyWorkerRequest,
  verifyWorkerRequestSignature,

  // Legacy export (deprecated, use validateWorkerRequest)
  checkWorkerRequest: validateWorkerRequest,
}
